"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.seoDomainAnalysis = exports.seoDomainAnalysisResultSchema = exports.seoDomainAnalysisArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Domain Analysis Action Types
var domainAnalysisActions = [
    'GetDomainOrganicKeywords', // Get domain's organic keywords and rankings
    'GetDomainCompetitors', // Get domain's organic competitors
    'CompareDomainsForSameKeyword', // Compare how up to 5 domains compete for same keywords
    'GetDomainAuthority', // Get domain and page authority scores
];
// Sort Types
var sortTypes = ['asc', 'desc'];
// Database Options
var databases = [
    'us',
    'uk',
    'ca',
    'ru',
    'de',
    'fr',
    'es',
    'it',
    'br',
    'au',
    'ar',
    'be',
    'ch',
    'dk',
    'fi',
    'hk',
    'ie',
    'il',
    'mx',
    'nl',
    'no',
    'pl',
    'se',
    'sg',
    'tr',
    'jp',
    'in',
];
exports.seoDomainAnalysisArgsSchema = zod_1.z.object({
    action: zod_1.z
        .enum(domainAnalysisActions)
        .describe('The type of domain analysis to perform:\n' +
        '• GetDomainOrganicKeywords: Get all organic keywords the domain ranks for with positions, search volume, and traffic data\n' +
        '• GetDomainCompetitors: Find domains that compete for the same organic keywords with overlap analysis\n' +
        '• CompareDomainsForSameKeyword: Compare up to 5 domains to see their ranking positions for shared keywords\n' +
        '• GetDomainAuthority: Get domain and page authority scores for SEO strength analysis'),
    domain: zod_1.z
        .string()
        .describe('The domain to analyze. Supports flexible URL formats - protocol is optional and will be auto-normalized (e.g., "example.com", "www.example.com", "https://blog.example.com"). URLs will be automatically normalized to domain format.'),
    competitorDomains: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('Competitor domains to compare with. Supports flexible URL formats - protocol is optional and will be auto-normalized (e.g., "competitor1.com", "www.competitor2.com"). Required for CompareDomainsForSameKeyword action.'),
    sortType: zod_1.z.enum(sortTypes).default('desc').describe('Sort order for results (default: desc)'),
    database: zod_1.z
        .enum(databases)
        .default('us')
        .describe('The geographic database to query (default: us)'),
    limit: zod_1.z
        .number()
        .min(1)
        .max(100)
        .default(10)
        .describe('Number of results to fetch (default 10, max 100). Keep at default unless user requests more data.'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the data in.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if the search returns no results. Default to true.'),
    })
        .describe('Search options'),
});
// Unified column definitions for each action type
var columnDefinitions = zod_1.z.object({
    GetDomainOrganicKeywords: zod_1.z
        .object({
        keyword: zod_1.z.string().describe('Keyword phrase'),
        position: zod_1.z.number().describe('Current position in search results'),
        previousPosition: zod_1.z.number().optional().describe('Previous position'),
        positionDifference: zod_1.z.number().optional().describe('Position difference from previous'),
        searchVolume: zod_1.z.number().describe('Monthly search volume'),
        costPerClick: zod_1.z.number().describe('Cost per click (CPC)'),
        url: zod_1.z.string().describe('URL ranking for this keyword'),
        trafficPercent: zod_1.z.number().describe('Traffic percentage'),
        trafficCostPercent: zod_1.z.number().describe('Traffic cost percentage'),
        competition: zod_1.z.number().describe('Competition level (0-1)'),
        numberOfResults: zod_1.z.number().describe('Number of search results'),
        trends: zod_1.z.string().optional().describe('Trend data'),
        keywordDifficulty: zod_1.z.number().describe('Keyword difficulty (0-100)'),
        searchIntent: zod_1.z.string().describe('Search intent classification'),
    })
        .describe('Columns for GetDomainOrganicKeywords: keyword rankings with traffic data'),
    GetDomainCompetitors: zod_1.z
        .object({
        domain: zod_1.z.string().describe('Competitor domain name'),
        competitorRelevance: zod_1.z.number().describe('Competitor relevance score'),
        commonKeywords: zod_1.z.number().describe('Number of common keywords'),
        organicKeywords: zod_1.z.number().describe('Competitor organic keywords count'),
        organicTraffic: zod_1.z.number().describe('Competitor organic traffic'),
        organicCost: zod_1.z.number().describe('Competitor organic cost'),
        adwordsKeywords: zod_1.z.number().describe('Competitor AdWords keywords count'),
    })
        .describe('Columns for GetDomainCompetitors: competitive analysis data'),
    CompareDomainsForSameKeyword: zod_1.z
        .object({
        keyword: zod_1.z.string().describe('Keyword phrase'),
        position0: zod_1.z.number().optional().describe('Position of domain 1 (main domain)'),
        position1: zod_1.z.number().optional().describe('Position of domain 2 (first competitor)'),
        position2: zod_1.z.number().optional().describe('Position of domain 3 (second competitor)'),
        position3: zod_1.z.number().optional().describe('Position of domain 4 (third competitor)'),
        position4: zod_1.z.number().optional().describe('Position of domain 5 (fourth competitor)'),
        numberOfResults: zod_1.z.number().describe('Number of search results'),
        costPerClick: zod_1.z.number().describe('Cost per click (CPC)'),
        searchVolume: zod_1.z.number().describe('Monthly search volume'),
        keywordDifficulty: zod_1.z.number().describe('Keyword difficulty (0-100)'),
        competition: zod_1.z.number().describe('Competition level (0-1)'),
        searchIntent: zod_1.z
            .string()
            .describe('Search intent classification. Note: Returns "unknown" as placeholder when data unavailable from API'),
        trends: zod_1.z.string().optional().describe('Trend data'),
    })
        .describe('Columns for CompareDomainsForSameKeyword: multi-domain position comparison'),
    GetDomainAuthority: zod_1.z
        .object({
        domain: zod_1.z.string().describe('Domain name'),
        authorityScore: zod_1.z.number().describe('Domain Authority score (0-100)'),
    })
        .describe('Columns for GetDomainAuthority: domain authority score'),
});
exports.seoDomainAnalysisResultSchema = zod_1.z.object({
    csvData: zod_1.z
        .string()
        .describe('Semicolon-delimited CSV containing all domain intelligence data. GetDomainOrganicKeywords: keyword, position, previous position, position difference, search volume, CPC, URL, traffic percent, traffic cost percent, competition, number of results, trends, keyword difficulty, search intent (multiple rows). GetDomainCompetitors: domain, competitor relevance, common keywords, organic keywords, organic traffic, organic cost, AdWords keywords (multiple rows). CompareDomainsForSameKeyword: keyword, position0-4 (one per domain), number of results, CPC, search volume, keyword difficulty, competition, search intent, trends (multiple rows). GetDomainAuthority: domain, authority score (1 row).'),
    query: zod_1.z.object({
        action: zod_1.z.string().describe('The analysis action that was performed'),
        domain: zod_1.z.string().describe('The domain that was analyzed'),
        competitorDomains: zod_1.z
            .array(zod_1.z.string())
            .optional()
            .describe('Competitor domains used in comparison (if applicable)'),
        database: zod_1.z.string().describe('The geographic database used'),
        limit: zod_1.z.number().describe('The limit parameter used'),
    }),
    columnSchema: zod_1.z
        .union([
        columnDefinitions.shape.GetDomainOrganicKeywords,
        columnDefinitions.shape.GetDomainCompetitors,
        columnDefinitions.shape.CompareDomainsForSameKeyword,
        columnDefinitions.shape.GetDomainAuthority,
        zod_1.z.record(zod_1.z.string()), // Allow simple key-value string pairs for backend compatibility
    ])
        .describe('Schema of the columns returned in csvData based on the action performed. Use this to understand the structure of each row in the CSV.'),
    metadata: zod_1.z.object({
        executedAt: zod_1.z.string().datetime(),
        recordCount: zod_1.z.number().optional().describe('Number of records returned'),
        actionType: zod_1.z
            .enum([
            'GetDomainOrganicKeywords',
            'GetDomainCompetitors',
            'CompareDomainsForSameKeyword',
            'GetDomainAuthority',
        ])
            .describe('The specific action type for column schema reference'),
    }),
});
exports.seoDomainAnalysis = (0, common_1.createEmitModeToolMetadata)({
    id: 'seoDomainAnalysis',
    displayName: 'Domain Intelligence',
    displayDescription: 'Comprehensive domain analysis with 4 key actions: GetDomainOrganicKeywords (keyword rankings & traffic), GetDomainCompetitors (competitive analysis), CompareDomainsForSameKeyword (multi-domain keyword comparison), GetDomainAuthority (domain & page authority scores). Perfect for competitive research, content gap analysis, and SEO strength evaluation.',
    category: 'seo',
    color: '#0066CC',
    backgroundColor: (0, common_1.createBgColor)('#0066CC'),
    icon: 'search',
    argsSchema: exports.seoDomainAnalysisArgsSchema,
}, exports.seoDomainAnalysisResultSchema);
